import { useQuery, UseQueryResult } from 'react-query';
import { IExposureHistory } from '../utils/exposureUtils';
import { getExposureHistory, getExposureHistoryByAccountAndMaterial } from '../sp-firebase/exposureHistoryFB';
import { useExposureHistoryStore } from '../sp-types/exposureHistoryStore';
import { GENERAL_ID } from '../components/Stores/exposureDataStore';

interface UseExposureHistoryOptions {
  accountId: string;
  materialId?: string;
  fromDate: Date;
  enabled?: boolean;
}

export const useExposureHistory = ({
  accountId,
  materialId,
  fromDate,
  enabled = true,
}: UseExposureHistoryOptions): UseQueryResult<IExposureHistory[], Error> => {
  const { setResults, setIsFetching, setError } = useExposureHistoryStore();

  const queryKey = materialId
    ? ['exposureHistory', accountId, materialId, fromDate.toISOString()]
    : ['exposureHistory', accountId, fromDate.toISOString()];

  const queryFn = async (): Promise<IExposureHistory[]> => {
    try {
      setIsFetching(true);
      setError(null);

      const result =
        materialId === GENERAL_ID
          ? await getExposureHistory(accountId, fromDate)
          : materialId
          ? await getExposureHistoryByAccountAndMaterial(accountId, materialId, fromDate)
          : undefined;

      const exposureHistory = result || [];
      setResults(exposureHistory);

      return exposureHistory;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch exposure history';
      setError(errorMessage);
      throw error;
    } finally {
      setIsFetching(false);
    }
  };

  return useQuery(queryKey, queryFn, {
    enabled: enabled && !!accountId,
    staleTime: 3 * 60 * 60 * 1000, // 3 hours
    cacheTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    retry: 2,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Convenience hook for fetching exposure history for a specific material
export const useExposureHistoryByMaterial = (
  accountId: string,
  materialId: string,
  fromDate: Date,
  enabled?: boolean,
) => {
  return useExposureHistory({ accountId, materialId, fromDate, enabled });
};

// Convenience hook for fetching all exposure history for an account
export const useExposureHistoryByAccount = (accountId: string, fromDate: Date, enabled?: boolean) => {
  return useExposureHistory({ accountId, fromDate, enabled });
};
